import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {AsyncPipe, NgIf} from "@angular/common";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatFormField} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {SnackbarService} from "../../../services/snackbar.service";
import {IEmailBlast} from "../../../interfaces/IEmailBlast";
import {ActiveUserService} from "../../../services/active-user.service";
import {EmailBlastService} from "../../../services/email-blast.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {DialogBaseComponent} from "../dialog-base/dialog-base.component";
import {MatCheckbox} from "@angular/material/checkbox";
import {PlayersService} from "../../../services/players.service";
import {IPlayer} from "../../../interfaces/player/IPlayer";
import {MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow} from "@angular/material/chips";
import {MatIcon} from "@angular/material/icon";
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption
} from "@angular/material/autocomplete";
import {map, Observable, of, startWith, switchMap} from "rxjs";
import {MatSelect} from "@angular/material/select";
import {GameService} from "../../../services/game.service";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {IEmailPlayersDialogData} from "../../../interfaces/IEmailPlayersDialogData";
import {PermissionsService} from "../../../services/permissions.service";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {chooseEmailGames} from "./email-players-games";
import {catchError} from "rxjs/operators";
import {COMMA, ENTER} from "@angular/cdk/keycodes";

@Component({
  selector: 'app-email-blast',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    MatFormField,
    MatInputModule,
    MatButton,
    DialogBaseComponent,
    MatCheckbox,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatChipInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    AsyncPipe,
    MatSelect,
  ],
  templateUrl: './email-blast.component.html',
  styleUrl: './email-blast.component.css'
})
export class EmailBlastComponent implements OnInit{
  @ViewChild("emailAutoTrigger") emailAutocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild("emailAuto") emailAutocomplete!: MatAutocomplete;

  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;
  public players: IPlayer[] = [];
  public allEmailsForGame: string[] = [];
  public selectedEmails: string[] = [];
  public filteredEmails$: Observable<string[]> = of([]);
  public readonly separatorKeysCodes = [ENTER, COMMA];
  public games: IGameQueryResult[] = [];

  subjectControl = new FormControl<string|null>("", Validators.required);
  messageControl = new FormControl<string|null>("", Validators.required);
  sendToAllUsersControl = new FormControl<boolean>(false);
  shouldBypassUnsubscribeControl = new FormControl<boolean>(false);
  emailControl = new FormControl<string | null>(null);
  gameControl = new FormControl<string | null>(null, Validators.required);

  emailForm = new FormGroup({
    subject: this.subjectControl,
    message: this.messageControl,
    sendToAllUsers: this.sendToAllUsersControl,
    shouldByPassUnsubscribe: this.shouldBypassUnsubscribeControl,
    game: this.gameControl
  });

  constructor(private snackbarService: SnackbarService,
              private activeUserService: ActiveUserService,
              private emailBlastService: EmailBlastService,
              private playerService: PlayersService,
              private matDialogRef: MatDialogRef<EmailBlastComponent>,
              private errorHandlingService: ErrorHandlingService) {
  }

  private gameService = inject(GameService);
  private permissionsService = inject(PermissionsService);
  private dialogData: IEmailPlayersDialogData = inject(MAT_DIALOG_DATA);
  public singleGame: IGameQueryResult | undefined;

  ngOnInit() {
    const choice = chooseEmailGames(this.gameService.getAllAvailableGames(), this.dialogData,
      gameIdP => this.permissionsService.userHasPermissionForGame(PermissionTypes.SendEmailBlast, gameIdP));
    this.games = choice.games;
    this.singleGame = choice.singleGame;

    this.gameControl.valueChanges.pipe(
      switchMap(gameId => this.loadRecipientSuggestions(gameId))
    ).subscribe(emails => this.allEmailsForGame = emails);

    this.gameControl.setValue(choice.prefillGameId);
    this.filterEmailsOnValueChange();
  }

  // Suggestions only — the recipient autocomplete.
  private loadRecipientSuggestions(gameIdP: string | null): Observable<string[]> {
    this.players = [];
    this.allEmailsForGame = [];
    this.selectedEmails = [];
    this.resetAutocomplete();
    // Without ViewPlayerInfo the lookup is refused; the admin can still type addresses or send to everyone.
    if (!gameIdP || !this.permissionsService.userHasPermissionForGame(PermissionTypes.ViewPlayerInfo, gameIdP)) {
      return of([]);
    }

    return this.playerService.getPlayersBySearchParams({gameId: gameIdP, maskData: false}).pipe(
      map(result => {
        this.players = result.Players;
        return result.Players.map(player => player.Email).sort((a, b) => a.trim().localeCompare(b.trim()));
      }),
      catchError(() => {
        this.errorHandlingService.displayDialogLevelErrorMessage("Failed to fetch players");
        return of([]);
      })
    );
  }

  filterEmailsOnValueChange() {
    this.filteredEmails$ = this.emailControl.valueChanges.pipe(
      startWith(null),
      map((email: string | null) => (email ? this._filter(email) : this.allEmailsForGame.slice()))
    )
  }

  selectedChip(eventP: MatAutocompleteSelectedEvent) {
    if (this.selectedEmails.includes(eventP.option.value)) {
      return;
    }
    this.selectedEmails.push(eventP.option.viewValue);
    this.resetAutocomplete();
  }

  resetAutocomplete() {
    this.emailControl.setValue(null);
  }

  emailInputChanged(valueP: string) {
    this.emailAutocompleteTrigger.autocompleteDisabled = valueP.length < 1;
    this.emailAutocomplete._isOpen = valueP.length > 1;
  }

  addEmail(eventP: MatChipInputEvent) {
    let input = eventP.chipInput;
    let value = eventP.value;

    if (!this.selectedEmails.includes(value)) {
      value = value.trim();
      if (value.length > 0) {
        this.selectedEmails.push(value);
      }
    }

    if (input) {
      input.clear();
    }

    this.resetAutocomplete();
  }

  removeEmail(indexP: number) {
    this.selectedEmails.splice(indexP, 1);
  }

  sendEmailBlast() {
    if (!this.emailForm.valid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    if (!this.sendToAllUsersControl.value && this.selectedEmails.length == 0) {
      this.emailForm.markAllAsTouched();
      this.emailForm.updateValueAndValidity();
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    let command: IEmailBlast = {
      AdminId: this.activeUserService.activeUser().Id,
      GameId: this.gameControl.value!,
      Message: this.messageControl.value!,
      Subject: this.subjectControl.value!,
      ShouldByPassUnsubscribe: this.shouldBypassUnsubscribeControl.value!,
      SendToAllPlayersInGame: this.sendToAllUsersControl.value!,
      Recipients: this.selectedEmails
    };

    this.emailBlastService.sendEmailBlast(command).subscribe({
      next: () => {
        this.snackbarService.openSuccessfulSnackBar("Message Sent!");
        this.matDialogRef.close();
      },
      error: err => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayDialogLevelErrorMessage(err);
      }
    });

  }

  private _filter(value: string): string[]
  {
    const filterValue = value.toLowerCase();
    return this.allEmailsForGame.filter((email: string) => email.toLowerCase().includes(filterValue));
  }

  splitPastedEmails(eventP: ClipboardEvent) {
   this.selectedEmails = eventP.clipboardData!.getData("text").split(/\r?\n */).filter((email: string) => email.trim().length > 0);
  }
}
